import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, X, Loader as Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const GIPHY_PROXY_URL = `${SUPABASE_URL}/functions/v1/giphy-proxy`;

interface GifResult {
  id: string;
  title: string;
  url: string;
  previewUrl: string;
  width: number;
  height: number;
  originalUrl: string;
}

interface GifPickerProps {
  onSelect: (gif: GifResult) => void;
  isGlobalDarkMode: boolean;
}

const GifIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="2" y="7" width="20" height="10" rx="2" ry="2" />
    <path d="M8 11v2h2" />
    <path d="M8 11h0" />
    <path d="M12 11v4" />
    <path d="M16 11h-2v4h2" />
    <path d="M16 13h-1.5" />
  </svg>
);

async function fetchGifs(query: string, offset = 0): Promise<{ gifs: GifResult[]; total: number }> {
  const params = new URLSearchParams({ limit: '24', offset: String(offset) });
  if (query.trim()) params.set('q', query.trim());

  const res = await fetch(`${GIPHY_PROXY_URL}?${params}`, {
    headers: {
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) throw new Error('Failed to fetch GIFs');
  const data = await res.json();
  return { gifs: data.gifs || [], total: data.pagination?.total_count || 0 };
}

const GifPicker: React.FC<GifPickerProps> = ({ onSelect, isGlobalDarkMode }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [gifs, setGifs] = useState<GifResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const loadGifs = useCallback(async (searchQuery: string, currentOffset = 0, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);

    try {
      const { gifs: results, total } = await fetchGifs(searchQuery, currentOffset);
      setGifs(prev => append ? [...prev, ...results] : results);
      setOffset(currentOffset + results.length);
      setHasMore(currentOffset + results.length < total);
    } catch {
      setError('Could not load GIFs. Please check your Giphy API key.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadGifs('', 0, false);
  }, [open, loadGifs]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setOffset(0);
      loadGifs(query, 0, false);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, loadGifs]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !hasMore || loadingMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      loadGifs(query, offset, true);
    }
  }, [hasMore, loadingMore, query, offset, loadGifs]);

  const handleSelect = (gif: GifResult) => {
    onSelect(gif);
    setOpen(false);
    setQuery('');
  };

  const handleOpenChange = (val: boolean) => {
    setOpen(val);
    if (!val) setQuery('');
  };

  const panelBg = isGlobalDarkMode ? 'bg-card border-border' : 'bg-white border-gray-200';
  const inputBg = isGlobalDarkMode ? 'bg-background border-border text-foreground placeholder:text-muted-foreground focus-visible:ring-border' : '';
  const subTextColor = isGlobalDarkMode ? 'text-muted-foreground' : 'text-gray-500';

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "h-7 w-7 transition-colors",
            open
              ? isGlobalDarkMode ? "bg-accent text-foreground" : "bg-muted text-gray-900"
              : isGlobalDarkMode ? "text-muted-foreground hover:text-foreground hover:bg-accent" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
          )}
          title="Send a GIF"
        >
          <GifIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className={cn("w-[340px] p-0 border shadow-xl z-[200]", panelBg)}
      >
        <div className="flex flex-col h-[400px]">
          {/* Header */}
          <div className={cn("flex items-center gap-2 p-2 border-b", isGlobalDarkMode ? "border-border" : "border-gray-100")}>
            <div className="relative flex-1">
              <Search className={cn("absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none", subTextColor)} />
              <Input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search GIFs..."
                className={cn("pl-8 h-8 text-sm", inputBg)}
                autoFocus
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className={cn("absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 transition-colors", isGlobalDarkMode ? "text-muted-foreground hover:text-foreground" : "text-gray-400 hover:text-gray-600")}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          {/* Grid */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto p-2"
          >
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className={cn("h-6 w-6 animate-spin", subTextColor)} />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full px-4 text-center">
                <p className={cn("text-xs", subTextColor)}>{error}</p>
              </div>
            ) : gifs.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className={cn("text-xs", subTextColor)}>
                  {query ? 'No GIFs found' : 'No trending GIFs'}
                </p>
              </div>
            ) : (
              <>
                <div className="columns-2 gap-1.5 space-y-1.5">
                  {gifs.map((gif, index) => (
                    <button
                      key={`${gif.id}-${index}`}
                      onClick={() => handleSelect(gif)}
                      className="block w-full rounded-md overflow-hidden hover:opacity-90 active:scale-95 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-workspace-accent"
                      title={gif.title}
                    >
                      <img
                        src={gif.previewUrl || gif.url}
                        alt={gif.title}
                        className="w-full h-auto object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
                {loadingMore && (
                  <div className="flex justify-center py-3">
                    <Loader2 className={cn("h-4 w-4 animate-spin", subTextColor)} />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className={cn("flex items-center justify-end px-3 py-1.5 border-t", isGlobalDarkMode ? "border-border" : "border-gray-100")}>
            <span className={cn("text-[10px] font-medium tracking-wide", subTextColor)}>
              Powered by GIPHY
            </span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default GifPicker;
